/**
 * Import de fonds documentaire - Page d'administration.
 *
 * Permet d'importer des notices et exemplaires depuis un fichier CSV
 * via une simple dropzone.
 */

import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  useCreateImportCampaign,
  useUploadImportFile,
  useImportCampaign,
  type CampaignStatus,
} from "@/api/hooks";
import { FileUp, Loader2, CheckCircle2, AlertCircle, FileText, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const STATUS_LABELS: Record<CampaignStatus, string> = {
  pending: "En attente",
  analyzing: "Analyse en cours...",
  analyzed: "Analyse terminée",
  matching: "Recherche de correspondances...",
  enriching: "Enrichissement BnF...",
  paused: "En pause",
  creating: "Création des notices...",
  completed: "Import terminé",
  failed: "Échec",
  cancelled: "Annulé",
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export function ImportFondsPage() {
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const createCampaign = useCreateImportCampaign();
  const uploadFile = useUploadImportFile();
  const { data: campaignData } = useImportCampaign(campaignId ?? undefined);

  const campaign = campaignData?.campaign;
  const progress = campaignData?.progress;

  const isProcessing =
    createCampaign.isPending ||
    uploadFile.isPending ||
    (campaign && ["analyzing", "matching", "enriching", "creating"].includes(campaign.status));

  const handleFile = useCallback(
    async (file: File) => {
      // Validate file type
      if (!file.name.endsWith(".csv") && !file.name.endsWith(".CSV")) {
        setError("Seuls les fichiers CSV sont acceptés");
        return;
      }

      setSelectedFile(file);
      setError(null);

      try {
        // Create campaign with file name
        const campaignName = `Import ${new Date().toLocaleDateString("fr-FR")} - ${file.name}`;
        console.log("[Import] Creating campaign:", campaignName);
        const result = await createCampaign.mutateAsync({ name: campaignName });
        const newCampaignId = result.campaign.id;
        console.log("[Import] Campaign created:", newCampaignId);
        setCampaignId(newCampaignId);

        // Upload file - this triggers async analysis automatically
        console.log("[Import] Uploading file:", file.name, file.size);
        await uploadFile.mutateAsync({ campaignId: newCampaignId, file });
        console.log("[Import] File uploaded, analysis started in background");

        // The useImportCampaign hook will poll for status updates
        // When status becomes "analyzed", user can proceed to matching
      } catch (err) {
        console.error("[Import] Error:", err);
        const message = err instanceof Error ? err.message : String(err);
        setError(`Erreur: ${message}`);
      }
    },
    [createCampaign, uploadFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        void handleFile(file);
      }
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        void handleFile(file);
      }
    },
    [handleFile]
  );

  const handleViewDetails = () => {
    if (campaignId) {
      void navigate(`/import/${campaignId}`);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setCampaignId(null);
    setError(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Import de fonds documentaire</h1>
        <p className="text-muted-foreground">
          Importez des notices et exemplaires depuis un fichier CSV
        </p>
      </div>

      {/* Main content */}
      <div className="mx-auto max-w-2xl">
        {!campaign ? (
          /* Dropzone */
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={cn(
              "relative rounded-xl border-2 border-dashed p-12 text-center transition-colors",
              isDragging
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-muted-foreground/50",
              isProcessing && "pointer-events-none opacity-50"
            )}
          >
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="absolute inset-0 cursor-pointer opacity-0"
              disabled={isProcessing}
            />

            <div className="flex flex-col items-center gap-4">
              {isProcessing ? (
                <Loader2 className="text-primary h-12 w-12 animate-spin" />
              ) : (
                <div className="bg-primary/10 rounded-full p-4">
                  <FileUp className="text-primary h-8 w-8" />
                </div>
              )}

              <div>
                <p className="text-lg font-medium">
                  {isProcessing ? "Traitement en cours..." : "Glissez votre fichier CSV ici"}
                </p>
                <p className="text-muted-foreground mt-1 text-sm">
                  {isProcessing ? selectedFile?.name : "ou cliquez pour sélectionner un fichier"}
                </p>
              </div>

              {selectedFile && !isProcessing && (
                <div className="bg-muted flex items-center gap-2 rounded-lg px-4 py-2">
                  <FileText className="h-4 w-4" />
                  <span className="text-sm font-medium">{selectedFile.name}</span>
                  <span className="text-muted-foreground text-sm">
                    ({formatFileSize(selectedFile.size)})
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Progress / Result */
          <div className="bg-card rounded-xl border p-8">
            <div className="flex flex-col items-center gap-6">
              {/* Status icon */}
              {campaign.status === "completed" ? (
                <div className="rounded-full bg-green-100 p-4 dark:bg-green-900/30">
                  <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
              ) : campaign.status === "failed" ? (
                <div className="rounded-full bg-red-100 p-4 dark:bg-red-900/30">
                  <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
                </div>
              ) : (
                <Loader2 className="text-primary h-12 w-12 animate-spin" />
              )}

              {/* Status text */}
              <div className="text-center">
                <p className="text-lg font-medium">{STATUS_LABELS[campaign.status]}</p>
                {selectedFile && (
                  <p className="text-muted-foreground mt-1 text-sm">{selectedFile.name}</p>
                )}
              </div>

              {/* Progress bar */}
              {progress && !["completed", "failed", "cancelled"].includes(campaign.status) && (
                <div className="w-full max-w-md">
                  <div className="bg-muted h-2 overflow-hidden rounded-full">
                    <div
                      className="bg-primary h-full transition-all duration-300"
                      style={{ width: `${String(Math.min(progress.progress, 100))}%` }}
                    />
                  </div>
                  <p className="text-muted-foreground mt-2 text-center text-sm">
                    {progress.processedRows} / {progress.totalRows} lignes traitées
                  </p>
                </div>
              )}

              {/* Stats for completed */}
              {campaign.status === "completed" && (
                <div className="grid w-full max-w-md grid-cols-2 gap-4">
                  <div className="bg-muted rounded-lg p-4 text-center">
                    <p className="text-2xl font-semibold">{campaign.counts.createdRecords}</p>
                    <p className="text-muted-foreground text-sm">Notices créées</p>
                  </div>
                  <div className="bg-muted rounded-lg p-4 text-center">
                    <p className="text-2xl font-semibold">{campaign.counts.createdItems}</p>
                    <p className="text-muted-foreground text-sm">Exemplaires créés</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                {["completed", "failed", "cancelled"].includes(campaign.status) && (
                  <Button variant="outline" onClick={handleReset}>
                    Nouvel import
                  </Button>
                )}
                <Button onClick={handleViewDetails}>
                  Voir les détails
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="bg-destructive/10 text-destructive mt-4 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* Help text */}
        <div className="bg-muted/50 mt-6 rounded-lg p-4">
          <h3 className="font-medium">Format attendu</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            Fichier CSV avec séparateur point-virgule (;) et les colonnes :
          </p>
          <p className="text-muted-foreground mt-2 font-mono text-xs">
            Notice_biblio; Titre; Auteur; Annee; Code_barres; Cote; Localisation; Statut...
          </p>
        </div>
      </div>
    </div>
  );
}
